import {
  Activity,
  Bell,
  ChevronRight,
  Clapperboard,
  HardDrive,
  MapPin,
  Plane,
  Share2,
  Smartphone,
  User,
  Users,
  Video,
} from "lucide-react";
import { useEffect, useState } from "react";
import { get, type Me, type Trip } from "../lib/api.ts";
import { nativeBridge, type Bridge } from "../lib/bridge.ts";
import { Link, navigate, tripPath, type TripSection } from "../lib/router.tsx";
import { isAdmin } from "../lib/rights.ts";
import { Card, ICON } from "../ui/index.ts";

type Row = { section: TripSection; label: string; hint: string; icon: typeof Users };

/**
 * The settings tab: an index, and only an index. Every setting the trip has is a row that opens a
 * page of its own, and nothing on this page acts where it stands.
 *
 * It used to be eight cards deep, mixing three kinds of thing in one costume — a control (the
 * trip picker), a handful of destinations, and four panels sitting open (your name and colour,
 * the capture switch, your sharing, sign out). Everything wore the same border and padding, so
 * ending the trip's capture read as no more weighty than switching trips, and "You" appeared
 * twice, seventy lines apart. A reader could not learn a rule for where to look.
 *
 * The rows are grouped by whose setting each one is, nearest the reader first: **You**, then
 * **This phone**, then **This trip**. A row that changes something for everybody on the trip is
 * then visibly not a row that changes something of yours. The groups also keep the page the same
 * shape for everyone — an admin sees more rows, not a different page.
 *
 * The trip picker stays a control at the top, because it is the one thing here you use and stay
 * put. "This phone" is the native app's only: a browser has nothing to set, so rather than an
 * empty group there is no group.
 */
export function SettingsPage({
  trip,
  me,
  bridge = nativeBridge(),
}: {
  trip: Trip;
  me: Me;
  /** The native side, when there is one. Only the app has a phone to set. */
  bridge?: Bridge | null;
}) {
  const admin = isAdmin(trip.roles);

  const you: Row[] = [
    { section: "profile", label: "Profile", hint: "Your name and your colour", icon: User },
    {
      section: "reminders",
      label: "Reminders",
      hint: "What people have asked you for",
      icon: Bell,
    },
    {
      section: "sharing",
      label: "Your sharing",
      hint: "Your footage and your route",
      icon: Share2,
    },
  ];

  const thisTrip: Row[] = [
    // Capture is the trip's own lifecycle, so it is only an admin's, and only while there is
    // something left to do with it: a trip that has ended has no switch to offer.
    ...(admin && (trip.state === "DRAFT" || trip.state === "ACTIVE")
      ? [
          {
            section: "capture" as const,
            label: "Capture",
            hint:
              trip.state === "DRAFT"
                ? "Start the trip, so footage is collected"
                : "End capture when the trip is over",
            icon: Video,
          },
        ]
      : []),
    ...(trip.mapLayersEnabled
      ? [
          {
            section: "places" as const,
            label: "Places and categories",
            hint: "Find a place, edit the categories, pull in the Google Maps list",
            icon: MapPin,
          },
        ]
      : []),
    {
      section: "people",
      label: "People & Sync",
      hint: "Who is on the trip and what they have sent",
      icon: Users,
    },
    {
      section: "deliverables",
      label: "Deliverables",
      hint: "What the trip owes, and its state",
      icon: Clapperboard,
    },
    ...(admin
      ? [
          {
            section: "storage" as const,
            label: "Storage",
            hint: "Where the footage lands, and how much is left",
            icon: HardDrive,
          },
        ]
      : []),
    { section: "activity", label: "Activity", hint: "Everything that happened", icon: Activity },
  ];

  return (
    <div className="stack">
      <TripSwitcher trip={trip} me={me} />
      <Rows title="You" rows={you} tripId={trip.id} />
      {bridge !== null && (
        <Rows
          title="This phone"
          rows={[
            {
              section: "phone",
              label: "Recording and permissions",
              hint: "What this phone has to allow for your route to keep recording",
              icon: Smartphone,
            },
          ]}
          tripId={trip.id}
        />
      )}
      <Rows title="This trip" rows={thisTrip} tripId={trip.id} />
    </div>
  );
}

/** One group: a heading, and the rows under it. The heading names whose settings these are. */
function Rows({ title, rows, tripId }: { title: string; rows: Row[]; tripId: string }) {
  return (
    <Card title={title} label={title}>
      <ul className="tile-list" aria-label={title}>
        {rows.map(({ section, label, hint, icon: Icon }) => (
          <li key={section}>
            <Link className="tile" to={tripPath(tripId, section)}>
              <span className="tile-mark">
                <Icon {...ICON} aria-hidden="true" />
              </span>
              <span className="tile-text">
                <span className="tile-label">{label}</span>
                <span className="tile-hint">{hint}</span>
              </span>
              <ChevronRight {...ICON} aria-hidden="true" className="tile-go" />
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * Switching trips. A native select, so the phone opens its own picker and the control is
 * reachable by keyboard and screen reader without a menu of our own. Names need a fetch per
 * membership — `me.memberships` carries only ids — so the current trip stands alone until they
 * land, and the control never renders empty.
 */
function TripSwitcher({ trip, me }: { trip: Trip; me: Me }) {
  const [trips, setTrips] = useState<{ id: string; name: string }[]>([
    { id: trip.id, name: trip.name },
  ]);

  useEffect(() => {
    let live = true;
    const others = me.memberships.filter((m) => m.tripId !== trip.id);
    if (others.length === 0) return;
    Promise.all(
      others.map((m) =>
        get<Trip>(`/v1/trips/${m.tripId}`)
          .then((t) => ({ id: t.id, name: t.name }))
          .catch(() => null),
      ),
    )
      .then((rows) => {
        if (!live) return;
        const found = rows.filter((r): r is { id: string; name: string } => r !== null);
        setTrips([{ id: trip.id, name: trip.name }, ...found]);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [trip.id, trip.name, me.memberships]);

  return (
    <Card>
      <label className="field trip-switch">
        <span>Trip</span>
        <select value={trip.id} onChange={(e) => navigate(tripPath(e.target.value, "settings"))}>
          {trips.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </label>
      <div className="row wrap">
        <Link className="button small" to="/">
          <Plane {...ICON} aria-hidden="true" />
          All trips
        </Link>
      </div>
    </Card>
  );
}
